import { useEffect, useMemo, useState } from 'react'

import type { ConfigurationFileContextType } from '@/features/configuration/contexts/ConfigurationFileContext'
import { useControllerConfigurationFileSetContext } from '@/features/controller/configuration/contexts/ControllerConfigurationFileSetContext'
import { useFetchNewerVersion } from '@/features/controller/configuration/hooks/useFetchNewerVersion'
import { useMultiModalConfiguration } from '@/features/controller/configuration/hooks/useMultiModalConfiguration'
import type {
  ControllerConfigurationModel,
  ControllerConfigurationModelUpdate,
  ControllerConfigurationSourceUpdate,
  LatestConfigurationVersions,
  UnsavedConfiguration,
} from '@/features/controller/configuration/models/controllerConfiguration'
import { isTillquistEnabled } from '@/features/controller/configuration/models/raspiProperties/tillquist'
import type { ControllerConfigVersion } from '@/features/controller/configuration/types'
import { isSaveConflict } from '@/features/controller/configuration/utils/saveConflict'

type useEditableControllerConfigurationModelParams = {
  controllerId: string
  onNewPowerMeterAdded: () => void
}

type ConfigurationFileKey = 'appJson' | 'raspiProperties'

export type BaseVersion = {
  /** The version the edits apply to, and the one a save replaces */
  version: ControllerConfigVersion | null
  /** A save conflicted, so this is the version saved meanwhile, which the draft would overwrite */
  replaced: boolean
}

export type BaseVersions = Record<ConfigurationFileKey, BaseVersion>

type PersistOptions = {
  /** app.json was saved, but raspi-custom.properties then conflicted or failed */
  onPartiallySaved?: () => void
}

type UnsavedChanges = {
  appJson?: UnsavedConfiguration
  raspiProperties?: UnsavedConfiguration
}

type UseEditableControllerConfigurationModel = {
  baseVersions: BaseVersions
  configurationModel: ControllerConfigurationModel
  /** The file has edits and a version newer than their base is known, so saving will ask to overwrite it */
  hasNewerVersion: Record<ConfigurationFileKey, boolean>
  isLoading: boolean
  /** A save conflicted and the version saved meanwhile is being fetched */
  isFetchingNewerVersion: boolean
  latestConfigurationVersions: LatestConfigurationVersions
  onConfigurationModelUpdated: (update: ControllerConfigurationModelUpdate) => void
  onConfigurationSourceUpdated: (update: ControllerConfigurationSourceUpdate) => void
  discardUnsavedChanges: () => void
  /** 'rebased' when a save conflicted and the edits now apply to the version saved meanwhile */
  persistUnsavedChanges: (options?: PersistOptions) => Promise<'saved' | 'rebased'>
  /** Stored with every version that the next save creates */
  comment: string
  setComment: (comment: string) => void
  /** Exposes the source code as unsaved changes if there are any */
  unsavedChanges: UnsavedChanges
  /** Exposes the source code regardless if there are unsaved changes or not */
  sourceView: ControllerConfigurationSourceUpdate
}

function hasConfigurationModelTillquistEnabled(configurationModel: ControllerConfigurationModel) {
  return configurationModel.raspiPropertiesConfig
    ? isTillquistEnabled(configurationModel.raspiPropertiesConfig.tillquistConfig)
    : false
}

function baseVersionsOf(versions: LatestConfigurationVersions): BaseVersions {
  return {
    appJson: { version: versions.appJson, replaced: false },
    raspiProperties: { version: versions.raspiProperties, replaced: false },
  }
}

export function useEditableControllerConfigurationModel({
  controllerId,
  onNewPowerMeterAdded,
}: useEditableControllerConfigurationModelParams): UseEditableControllerConfigurationModel {
  const { appJson, raspiProperties } = useControllerConfigurationFileSetContext()
  const { fetchNewerVersion, isFetchingNewerVersion } = useFetchNewerVersion(controllerId)
  const latestConfigurationVersions: LatestConfigurationVersions = {
    appJson: appJson.latestVersion,
    raspiProperties: raspiProperties.latestVersion,
  }
  const isFetched = appJson.isLatestVersionFetched && raspiProperties.isLatestVersionFetched
  const isLoading = appJson.isLatestVersionLoading || raspiProperties.isLatestVersionLoading

  const { multiModalConfiguration, applyConfigurationModelUpdate, applyConfigurationSourceUpdate } =
    useMultiModalConfiguration({
      initialSource: isFetched
        ? {
            appJsonText: latestConfigurationVersions.appJson?.value,
            raspiPropertiesText: latestConfigurationVersions.raspiProperties?.value,
          }
        : {},
    })
  const { model: configurationModel, source: configurationSourceText } = multiModalConfiguration

  const [comment, setComment] = useState<string>('')
  const [notifyIfTillquistMeterAdded, setNotifyIfTillquistMeterAdded] = useState(false)
  // Null until the configuration first loads; until then the latest versions are the base
  const [loadedBaseVersions, setLoadedBaseVersions] = useState<BaseVersions | null>(null)
  const baseVersions = loadedBaseVersions ?? baseVersionsOf(latestConfigurationVersions)

  function readConfigurationStateFromSource() {
    setLoadedBaseVersions(baseVersionsOf(latestConfigurationVersions))
    const loadedModel = applyConfigurationSourceUpdate({
      appJsonText: latestConfigurationVersions.appJson?.value,
      raspiPropertiesText: latestConfigurationVersions.raspiProperties?.value,
    })
    // If we didn't previously have Tillquist configured, we should notify if it does get added.
    // This reads the model just loaded, not `configurationModel`, which is still the previous
    // render's value here and on the first load is the empty initial one -- which armed the
    // notification for every controller that already had Tillquist enabled.
    setNotifyIfTillquistMeterAdded(!hasConfigurationModelTillquistEnabled(loadedModel))
    // This handles the case where UI hasn't been used for any updates but there are still changes
    setComment('Configuration improvement suggested by FlexPortal')
  }

  /**
   * A newer version becomes a file's base only if the file has no edits, or if its draft is that version
   * (our own save). Otherwise the edits stay, and saving them conflicts and rebases.
   */
  function adoptLatestVersions() {
    const drafts = {
      appJson: configurationSourceText.appJsonText,
      raspiProperties: configurationSourceText.raspiPropertiesText,
    }
    const adoptable = (key: ConfigurationFileKey) =>
      drafts[key] === baseVersions[key].version?.value || drafts[key] === latestConfigurationVersions[key]?.value
    if (adoptable('appJson') && adoptable('raspiProperties')) {
      readConfigurationStateFromSource()
      return
    }
    const keys = (['appJson', 'raspiProperties'] as const).filter(adoptable)
    setLoadedBaseVersions((current) => {
      const next = { ...(current ?? baseVersions) }
      keys.forEach((key) => (next[key] = { version: latestConfigurationVersions[key], replaced: false }))
      return next
    })
    keys.forEach((key) => (drafts[key] = latestConfigurationVersions[key]?.value))
    applyConfigurationSourceUpdate({ appJsonText: drafts.appJson, raspiPropertiesText: drafts.raspiProperties })
  }

  useEffect(() => {
    if (!isFetched) {
      return
    }
    if (loadedBaseVersions === null) {
      readConfigurationStateFromSource()
    } else {
      adoptLatestVersions()
    }
  }, [latestConfigurationVersions.appJson, latestConfigurationVersions.raspiProperties, isFetched])

  const { unsavedRaspiProperties, unsavedAppJson } = useMemo(() => {
    return {
      unsavedAppJson:
        configurationSourceText.appJsonText !== baseVersions.appJson.version?.value
          ? configurationSourceText.appJsonText
          : undefined,
      unsavedRaspiProperties:
        configurationSourceText.raspiPropertiesText !== baseVersions.raspiProperties.version?.value
          ? configurationSourceText.raspiPropertiesText
          : undefined,
    }
  }, [
    baseVersions.appJson.version?.value,
    baseVersions.raspiProperties.version?.value,
    configurationSourceText.appJsonText,
    configurationSourceText.raspiPropertiesText,
  ])

  function hasNewerVersionThanBase(key: ConfigurationFileKey, unsaved: string | undefined) {
    const latest = latestConfigurationVersions[key]
    const base = baseVersions[key]
    if (unsaved === undefined) {
      return false
    }
    // A draft equal to the latest version is our own save, about to become the base
    return base.replaced || (latest !== null && latest.id !== base.version?.id && unsaved !== latest.value)
  }

  function onConfigurationModelUpdated({
    updateAppConfig,
    updateRaspiPropertiesConfig,
    comment: updateComment,
  }: ControllerConfigurationModelUpdate) {
    applyConfigurationModelUpdate({ updateAppConfig, updateRaspiPropertiesConfig })
    if (updateComment && (updateAppConfig || updateRaspiPropertiesConfig)) {
      setComment(updateComment)
    }
  }

  function onConfigurationSourceUpdated({
    appJsonText,
    comment: updateComment,
    raspiPropertiesText,
  }: ControllerConfigurationSourceUpdate) {
    applyConfigurationSourceUpdate({ appJsonText, raspiPropertiesText })
    const changesAppJson = appJsonText && appJsonText !== baseVersions.appJson.version?.value
    const changesRaspiProperties =
      raspiPropertiesText && raspiPropertiesText !== baseVersions.raspiProperties.version?.value
    if (updateComment && (changesAppJson || changesRaspiProperties)) {
      setComment(updateComment)
    }
  }

  /** Makes the version saved meanwhile the file's base; false if it could not be fetched */
  async function rebaseOnNewerVersion(key: ConfigurationFileKey) {
    const newerVersion = await fetchNewerVersion(baseVersions[key].version!.configurationTypeId)
    if (!newerVersion) {
      return false
    }
    setLoadedBaseVersions((current) => ({
      ...(current ?? baseVersions),
      [key]: { version: newerVersion, replaced: true },
    }))
    return true
  }

  /** False if the save conflicted and the file was rebased */
  async function saveFile(key: ConfigurationFileKey, file: ConfigurationFileContextType, value: string | undefined) {
    if (value === undefined) {
      return true
    }
    try {
      await file.updateAndInvalidate({
        previousVersionId: baseVersions[key].version!.id,
        comment: comment.trim() || undefined,
        value,
      })
      return true
    } catch (error) {
      if (isSaveConflict(error) && (await rebaseOnNewerVersion(key))) {
        return false
      }
      throw error
    }
  }

  async function persistUnsavedChanges({ onPartiallySaved }: PersistOptions = {}) {
    if (!unsavedAppJson && !unsavedRaspiProperties) {
      return 'saved'
    }
    // One file at a time: after a conflict the other file waits, and saves with the next attempt
    if (!(await saveFile('appJson', appJson, unsavedAppJson))) {
      return 'rebased'
    }
    const isAppJsonSaved = unsavedAppJson !== undefined
    let saved: boolean
    try {
      saved = await saveFile('raspiProperties', raspiProperties, unsavedRaspiProperties)
    } catch (error) {
      if (isAppJsonSaved) {
        onPartiallySaved?.()
      }
      throw error
    }
    if (!saved) {
      if (isAppJsonSaved) {
        onPartiallySaved?.()
      }
      return 'rebased'
    }
    if (notifyIfTillquistMeterAdded && hasConfigurationModelTillquistEnabled(configurationModel)) {
      onNewPowerMeterAdded()
    }
    return 'saved'
  }

  const sourceView: ControllerConfigurationSourceUpdate = {
    appJsonText: unsavedAppJson ?? baseVersions.appJson.version?.value,
    raspiPropertiesText: unsavedRaspiProperties ?? baseVersions.raspiProperties.version?.value,
  }

  const unsavedChanges: UnsavedChanges = {
    appJson: unsavedAppJson ? { value: unsavedAppJson } : undefined,
    raspiProperties: unsavedRaspiProperties ? { value: unsavedRaspiProperties } : undefined,
  }

  return {
    baseVersions,
    comment,
    configurationModel,
    discardUnsavedChanges: readConfigurationStateFromSource,
    hasNewerVersion: {
      appJson: hasNewerVersionThanBase('appJson', unsavedAppJson),
      raspiProperties: hasNewerVersionThanBase('raspiProperties', unsavedRaspiProperties),
    },
    isFetchingNewerVersion,
    isLoading,
    latestConfigurationVersions,
    onConfigurationModelUpdated,
    onConfigurationSourceUpdated,
    persistUnsavedChanges,
    setComment,
    sourceView,
    unsavedChanges,
  }
}
